// controlPanel — the derivations the command centre needs and nothing else has.
//
// Every function here is PURE: no React, no Firestore, no clock of its own. The
// current time always arrives as an argument, so a test can stand at 13:59 and
// again at 14:01 without mocking anything.
//
// The bar for adding something to this file is that no existing module already
// does it. `buildTimeline` (timeline.js) stays untouched and unmodified — the
// panel EXTENDS it by composition. Anything that could live there instead of
// here should live there instead of here.

import { to12h, hmToMinutes, SHIFT_BOUNDARIES } from "./timeline";
import { isMeal, BREAK_CHANNELS, coverLateAfter } from "./breaks";
import { GO_HOME_ALERT_MINUTES } from "./schedule";
import { alertSentAt } from "./alerts";
import { doubleBookedProviders, liveConflicts } from "./doubleBooking";
// domain/tasks imports only domain/stampTime, so this direction opens no cycle.
import { requiresConfirm, recipientsOf, pendingUids, answerOf, declineOf, AUTO_REASONS } from "./tasks";

// Re-exported so existing importers of controlPanel keep working; the
// implementation now lives in its own module.
export { connectionLevel, connectionState, staleLabel, RECONNECT_SOFT_MS, NOT_LIVE_MS } from "./panelConnection";

// How long the sheet may fail to reach the board before anybody is told.
//
// A refused cycle on its own is usually a person mid-edit — the 2026-09-14
// captures show a header cell flipping between `RELIEF` and blank four times in
// two hours. Twenty minutes is long enough that nobody is still typing and
// short enough that the board is not quietly hours behind.
export const SHEET_FEED_STALE_MIN = 20;

// The on-call departure sentinel used by buildTimeline (7 AM the next morning).
// Duplicated rather than exported from timeline.js so this module can be read
// without chasing a constant; kept in sync by `controlPanel.test.js`.
export const ON_CALL_DEPART_MIN = 31 * 60;

// ── Lanes ────────────────────────────────────────────────────────────────────

// The model groups rooms into sections; paging needs one flat, ordered list.
//
// Order is the board's own order — `groupRoomsByLocation` sorts by configured
// location then numeric-aware name — so the panel never invents an ordering of
// its own that would disagree with the board people already know.
export function flattenLanes(sections) {
  const list = Array.isArray(sections) ? sections : [];
  const out = [];
  for (const sec of list) {
    const rooms = (sec && Array.isArray(sec.rooms)) ? sec.rooms : [];
    for (const room of rooms) {
      if (!room || room.id == null) continue;
      out.push({
        roomId: room.id,
        roomName: room.name || String(room.id),
        sectionKey: sec.key,
        sectionLabel: sec.label,
        cases: Array.isArray(room.cases) ? room.cases : [],
      });
    }
  }
  return out;
}

export function laneIds(sections) {
  return flattenLanes(sections).map((l) => l.roomId);
}

// ── Coverage: does the staffing outlast the work? ────────────────────────────

// The pinned "coverage-to" column, per LANE. The model carries departures per
// CASE; a lane can hold several cases with different people on them, and the
// question the board-runner actually asks is about the ROOM: "is anybody still
// here when this room is still running?"
//
//   { departMin, departLabel, lastEndMin, shortfallMin, uncovered, onCall }
//
// `uncovered` is true when some case in the lane runs past the departure of the
// person assigned to it. `departMin` is then the FIRST moment cover lapses —
// not the latest departure — because the first gap is the one that needs a plan.
//
// A null departure is NOT treated as "stays all day". An unresolvable shift code
// means we do not know, and on a safety surface not knowing must not render as
// reassurance: it reports `departMin: null` and the caller shows UNKNOWN.
export function laneCoverage(lane) {
  const cases = (lane && Array.isArray(lane.cases)) ? lane.cases : [];
  if (cases.length === 0) {
    return { departMin: null, departLabel: "", lastEndMin: null, shortfallMin: 0, uncovered: false, onCall: false };
  }

  let lastEndMin = null;
  let firstGapMin = null;
  let maxShortfall = 0;
  let anyOnCall = false;
  let latestDepart = null;
  let anyKnownDepart = false;

  for (const c of cases) {
    if (!c) continue;
    if (c.onCall) anyOnCall = true;
    const end = Number.isFinite(c.endMin) ? c.endMin : null;
    if (end != null) lastEndMin = lastEndMin == null ? end : Math.max(lastEndMin, end);

    const depart = Number.isFinite(c.departMin) ? c.departMin : null;
    if (depart != null) {
      anyKnownDepart = true;
      latestDepart = latestDepart == null ? depart : Math.max(latestDepart, depart);
      if (end != null && depart < end) {
        firstGapMin = firstGapMin == null ? depart : Math.min(firstGapMin, depart);
        maxShortfall = Math.max(maxShortfall, end - depart);
      }
    }
  }


  const uncovered = firstGapMin != null;
  const departMin = uncovered ? firstGapMin : (anyKnownDepart ? latestDepart : null);
  return {
    departMin,
    departLabel: departMin == null ? "" : to12h(departMin),
    lastEndMin,
    shortfallMin: maxShortfall,
    uncovered,
    onCall: anyOnCall,
  };
}

// The monochrome shift tag for the pinned column.
//
// This deliberately REPLACES `shiftLabelColor` here. That function's 9 PM value
// is `#dc2626` — byte-identical to the app's `accent.error`. On a wall display
// "leaves at 9" must not render in the same red as "something is wrong", so the
// tag carries the fact as TEXT and red is reserved for the one thing that is
// genuinely wrong: cover lapsing before the work ends.
export function shiftTag(departMin, onCall = false) {
  if (onCall || departMin === ON_CALL_DEPART_MIN) return "ON CALL";
  if (!Number.isFinite(departMin)) return "UNKNOWN";
  return to12h(departMin);
}

// ── The burn-down ────────────────────────────────────────────────────────────

// How much of a case bar is already behind us, 0–100.
//
// `shiftHatchPercent` (timeline.js) has the same SHAPE but answers a different
// question — it measures the stretch the CRNA is still scheduled to be present.
// This measures elapsed time. They are not interchangeable.
//
// NOTE this is *scheduled* elapsed, not actual: the model has no real start
// event to key off (see the integration spec §4.1). A bar filling to 100% means
// the case was scheduled to be over, never that it is.
export function barBurnPercent(c, nowMin) {
  if (!c || !Number.isFinite(nowMin)) return 0;
  const { startMin, endMin } = c;
  if (!Number.isFinite(startMin) || !Number.isFinite(endMin)) return 0;
  const span = endMin - startMin;
  if (!(span > 0)) return nowMin >= endMin ? 100 : 0;
  return Math.max(0, Math.min(100, ((nowMin - startMin) / span) * 100));
}

// ── Alarm folding ────────────────────────────────────────────────────────────

// Severity order, highest first: emergency outranks uncovered outranks break.
export const LANE_ALARM_ORDER = ["emergency", "uncovered", "break"];

function inSet(collection, id) {
  if (!collection) return false;
  if (typeof collection.has === "function") return collection.has(id);
  if (Array.isArray(collection)) return collection.includes(id);
  return false;
}

// One room's combined alarm state.
//
// Folded ONCE, here, because four consumers need the identical answer — the
// lane wash, the rail chip counts, the pager's off-page line, and the
// outstanding-items list. Four independent re-derivations is four chances for
// the wall to contradict itself about whether a room is on fire.
export function laneAlarmState(roomId, sets = {}) {
  const emergency = inSet(sets.emergencyRoomIds, roomId);
  const uncovered = inSet(sets.uncoveredRoomIds, roomId);
  const breakRequested = inSet(sets.breakRequestRoomIds, roomId);
  const top = emergency ? "emergency" : uncovered ? "uncovered" : breakRequested ? "break" : null;
  return { emergency, uncovered, breakRequested, top };
}

// ── Boundaries ───────────────────────────────────────────────────────────────

// The NEXT shift boundary at or after `nowMin`, with its staffing row.
//
// Agent 1 cut the four-card staffing strip down to one card: three future
// boundaries is planning, and this screen is for commanding. Past the last
// boundary there is no next one — that returns null rather than wrapping round
// to 3 PM tomorrow and reporting a shortage that is 17 hours away.
export function nextBoundary(staffing, nowMin, doctorStaffing) {
  const rows = Array.isArray(staffing) ? staffing : [];
  if (!Number.isFinite(nowMin)) return null;
  const row = rows.find((r) => r && Number.isFinite(r.boundaryMin) && r.boundaryMin >= nowMin);
  if (!row) return null;
  const docRows = Array.isArray(doctorStaffing) ? doctorStaffing : [];
  const doctors = docRows.find((d) => d && d.boundaryMin === row.boundaryMin) || null;
  return { ...row, doctors, minutesAway: row.boundaryMin - nowMin };
}

// Every boundary still ahead that is short-staffed — for the outstanding-items
// list, which unlike the rail card does care about later trouble.
export function shortBoundariesAhead(staffing, nowMin) {
  const rows = Array.isArray(staffing) ? staffing : [];
  if (!Number.isFinite(nowMin)) return [];
  return rows.filter((r) => r && Number.isFinite(r.boundaryMin) && r.boundaryMin >= nowMin && r.delta < 0);
}

// ── Queues ───────────────────────────────────────────────────────────────────

// Oldest-first ordering for the alerts and breaks-waiting cards.
//
// ⚠️ `sentAt` is a `serverTimestamp()`, which is NULL on the SENDING device
// until the write round-trips. A null is therefore a normal transient, not bad
// data — and it must sort as the NEWEST entry (`?? now`), never the oldest.
// Sorting a null to the top would put the freshest request at the head of a
// queue whose entire purpose is "who has been waiting longest", and it would do
// it only on the requester's own screen, which is exactly the kind of bug that
// survives review.
export function alertQueue(alerts, nowMs) {
  const list = Array.isArray(alerts) ? alerts.filter(Boolean) : [];
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  const at = (a) => {
    const d = alertSentAt(a);
    const ms = d instanceof Date ? d.getTime() : null;
    return Number.isFinite(ms) ? ms : now;
  };
  return list
    .map((a, i) => ({ a, i, t: at(a) }))
    .sort((x, y) => (x.t - y.t) || (x.i - y.i))   // stable on ties
    .map((x) => x.a);
}

// ── Break windows ────────────────────────────────────────────────────────────

// Is this meal break overdue?
//
// Returns "due" ONLY when the channel is untouched, the facility configured a
// window for it, and that time has passed. Everything else is "none".
//
// FAILS CLOSED TO SILENCE, deliberately. `breakWindows` is optional and a fresh
// tenant has none. A panel that treated missing config as "flag everything"
// would cry wolf on day one at every new hospital, and an alarm nobody believes
// is worse than no alarm. Bathroom is never "due" — it is repeatable and
// untaken is not a deficit.
export function breakDueState(channelState, channel, breakWindows, nowMin) {
  if (channelState !== "none") return "none";
  if (!isMeal(channel)) return "none";
  if (!breakWindows || typeof breakWindows !== "object") return "none";
  const after = hmToMinutes(breakWindows[channel]);
  if (after == null) return "none";
  if (!Number.isFinite(nowMin)) return "none";
  return nowMin >= after ? "due" : "none";
}

// ── The rail chips ───────────────────────────────────────────────────────────

// The first-five-seconds answer: is anything on fire, and is anyone uncovered?
//
// FOUR TREATMENTS THAT SURVIVE GREYSCALE. Hue alone must never be the carrier —
// some clinicians are colour-blind, and a photograph of the wall is monochrome.
// So each chip differs in fill weight AND glyph AND position:
//
//   emergency → filled-dark    "!"   uncovered → thick-outline "∅"
//   break     → filled-light   "●"   short     → dashed        "▲"  (forecast)
//
// Dashed means FORECAST throughout the panel: the staffing chip describes a
// boundary that has not happened yet, and must not read as a live alarm.
//
// ALL FOUR CHIPS ALWAYS RENDER, in this order, even at zero. A chip that
// disappears when it reaches zero makes the row reflow, and a reader who has
// learned "uncovered is the second chip" then has to re-read the row every time
// something clears. Position is part of the information.
const CHIP_ORDER = ["emergency", "uncovered", "break", "short"];

const CHIP_DEF = {
  emergency: { glyph: "!", treatment: "filled-dark", noun: "EMERGENCY", plural: "EMERGENCIES" },
  uncovered: { glyph: "∅", treatment: "thick-outline", noun: "UNCOVERED", plural: "UNCOVERED" },
  break: { glyph: "●", treatment: "filled-light", noun: "BREAK WAITING", plural: "BREAKS WAITING" },
  short: { glyph: "▲", treatment: "dashed", noun: "SHORT", plural: "SHORT" },
};

function setSize(collection) {
  if (!collection) return 0;
  if (typeof collection.size === "number") return collection.size;
  if (Array.isArray(collection)) return collection.length;
  return 0;
}

export function panelChips(ctx = {}) {
  const {
    emergencyRoomIds,
    uncoveredRoomIds,
    breakRequestRoomIds,
    staffing,
    doctorStaffing,
    nowMin,
  } = ctx;

  const next = nextBoundary(staffing, nowMin, doctorStaffing);
  const shortBy = next && next.delta < 0 ? Math.abs(next.delta) : 0;

  const counts = {
    emergency: setSize(emergencyRoomIds),
    uncovered: setSize(uncoveredRoomIds),
    break: setSize(breakRequestRoomIds),
    short: shortBy,
  };

  return CHIP_ORDER.map((key) => {
    const def = CHIP_DEF[key];
    const count = counts[key];
    const active = count > 0;
    let label;
    if (key === "short") {
      // The staffing chip names the boundary, because "SHORT 2" without a time
      // reads as "right now" — which it is not.
      label = active ? `SHORT ${count} AT ${next.label}` : next ? `OK AT ${next.label}` : "NO BOUNDARY AHEAD";
    } else {
      label = `${count} ${count === 1 ? def.noun : def.plural}`;
    }
    return {
      key,
      count,
      active,
      label,
      glyph: def.glyph,
      // A calm chip keeps its slot but drops to the quiet treatment, so the
      // saturated pixels on screen are only ever the live alarms.
      treatment: active ? def.treatment : "calm",
      forecast: key === "short",
    };
  });
}

// ── Outstanding items ────────────────────────────────────────────────────────

// The closing card: everything still wanting a human, folded from inputs that
// were all derived elsewhere. Six rules, severity-ordered, empty categories
// omitted so the card shrinks to nothing on a clean board.
//
//   ctx = {
//     nowMin,
//     emergencies:      [{ roomId, roomName, acknowledgedBy }],
//     uncoveredRoomIds: Set|Array,
//     uncoveredRooms:   [{ roomId, roomName, providerName, reason, awayRoomName }]
//                       — the SAME rooms, described. Resolved from the on-call
//                       ASSIGNMENT rows by `coverageStatus`, so a room with no
//                       case on the chart still raises its alarm.
//     lanes:            flattenLanes(model.sections),
//     breakRows:        [{ roomId, roomName, providerName, breaks }],
//     breakWindows, staffing,
//     roomsMissingMd:   [{ roomId, roomName }],
//     placed:           timeline's flat cases, for the double-booking check
//     dayClockMin:      `nowMin`, but ONLY when the board on screen is TODAY —
//                       null on every other date. See below.
//   }
//
// ⚠️ TWO CLOCKS, AND THE SECOND ONE IS NOT REDUNDANT. `nowMin` is the raw
// minute-of-day this machine is at. `dayClockMin` is that same minute *if it
// describes the day being looked at*, which is what `claimClockFor` in
// useControlPanelData already computes for `rowClaimsAt`. Every caller of this
// module sits behind a date picker, so any rule that asks "is this over yet"
// must read `dayClockMin`: open tomorrow's board at 3 PM and a raw clock says
// the morning already happened. Null is the fail-closed answer — no clock means
// no evidence anything has finished, so nothing is suppressed.
// How long a directive may go unanswered before the board says so out loud.
//
// ⚠️ A JUDGEMENT, AND THE OWNER'S TO OVERRULE. Ten minutes: a break cover
// walking from another OR is a two-to-five minute walk, so ten without any
// answer means they most likely never saw it. Shorter nags at somebody who is
// simply on their way; much longer and the board finds out by sending a person
// to look. Deliberately NOT tuned to the shortest case — see the note on
// severity below: this is a warning, not an alarm.
export const UNANSWERED_AFTER_MIN = 10;

// And how long it stays on the worklist before the board stops saying it.
//
// ⚠️ A ROW THAT CAN NEVER BE CLEARED IS THE ALARM-FATIGUE FAILURE THIS FILE
// EXISTS TO AVOID. "Clear" on the task card hides a row from one person's view
// and writes nothing, a recipient who never opens the app never answers, and a
// cover called off writes no task update — so without a ceiling this item grows
// all day ("… · 430m"), cannot be dismissed, and teaches people to scroll past
// NEEDS YOU NOW, which is where unacknowledged emergencies live.
//
// ⚠️ AND THE ROOM IS STILL WATCHED — BY SOMETHING ELSE. This item means "nobody
// replied to the message", not "the room is uncovered". An uncovered room is
// tracked independently by `cover-not-arrived` above, from the room doc itself,
// which has no such ceiling. So retiring the message-level nag loses no clinical
// fact: after two hours "call them" has stopped being the useful instruction and
// the room's own state is the live one.
//
// ⚠️ WHICH IS EXACTLY WHY THIS APPLIES TO BREAK COVERS AND NOTHING ELSE.
// `cover-not-arrived` knows about breaks; it knows nothing about a hand-typed
// "come to WOR 3", a relief, a takeover or a go-home. Those have NO second
// watcher, so a ceiling on them would drop the fact outright rather than hand it
// over. `requiresConfirm` is true for every kind but `emergency_ack`, so gating
// on that instead of on the reason would have retired all of them.
export const UNANSWERED_STALE_MIN = 120;

// How long a directive has gone unanswered, in minutes.
//
// ⚠️ AN INSTANT MINUS AN INSTANT — NEVER minutes-past-midnight minus
// minutes-past-midnight. This first compared `nowMin` with the send time's
// hour-and-minute, which breaks in two ways that both HIDE an overdue
// directive rather than showing one: a directive sent at 23:55 and read at
// 00:05 computed -1430 and fell straight through the "not old enough yet"
// guard, disappearing at the exact moment it was most overdue; and because
// `nowMin` is device-local while `dateKey` is the facility's timezone, a phone
// in another zone shifted every age by the offset in one direction or the
// other. Elapsed milliseconds have neither problem.
//
// Firestore hands `createdAt` back as a Timestamp, a Date, or — for one frame
// on the local echo of a write — null. Null returns null rather than 0: a
// directive whose stamp has not landed is one frame old, not overdue since
// midnight.
function taskWaitedMin(task, nowMs) {
  if (!Number.isFinite(nowMs)) return null;
  const t = task?.createdAt;
  if (!t) return null;
  let d = null;
  if (typeof t.toDate === "function") d = t.toDate();
  else if (t instanceof Date) d = t;
  else if (typeof t.seconds === "number") d = new Date(t.seconds * 1000);
  else if (typeof t === "number") d = new Date(t);
  if (!d || Number.isNaN(d.getTime())) return null;
  return Math.floor((nowMs - d.getTime()) / 60000);
}

export function outstandingItems(ctx = {}) {
  const {
    nowMin,
    dayClockMin = null,
    emergencies = [],
    uncoveredRoomIds,
    uncoveredRooms = [],
    lanes = [],
    breakRows = [],
    breakWindows,
    staffing = [],
    roomsMissingMd = [],
    placed = [],
    reliefRows = [],
    // Today's directives, for the unanswered escalation below. Defaults empty,
    // so every existing caller behaves exactly as it did.
    tasks = [],
    nameByUid = {},
    // A real instant, for the unanswered-directive age. Without one no age can
    // be claimed, so that escalation simply does not run — the same "no clock,
    // say nothing" rule the rest of this file follows.
    nowMs = null,
    // Names on today's sheet that the import could not match to a person
    // (hooks/useSheetEngineQuestions). Defaults empty, so every existing caller
    // behaves exactly as it did.
    sheetQuestions = [],
    // Whether the sheet is still REACHING the board — `{ ok, reason, atMs,
    // lastOkMs }` from hooks/useSheetEngineQuestions. Null for every caller that
    // does not read the engine, which then behaves exactly as it did.
    sheetFeed = null,
  } = ctx;

  const items = [];

  // A HANDOVER THAT HAS BEEN PROMISED BUT NOT COMPLETED.
  // Staging a relief tells two people something is about to happen and then
  // relies on somebody remembering to finish it. Until the takeover is
  // committed the room has two names against it and only one person in it, so
  // it belongs on the list of things still wanting a human — and it disappears
  // on its own the moment the takeover happens, because the staged fields clear.
  //
  // ⚠️ "STALE" IS NOT MERELY OLD. `stagedReliefRows` also marks a handover stale
  // when the OUTGOING person's departure time has already passed: the room is
  // then covered by somebody the board does not believe is there, which is a
  // different and worse problem than a handover nobody has got round to. That
  // case is ACT NOW; an ordinary pending one is PLAN.
  for (const r of Array.isArray(reliefRows) ? reliefRows : []) {
    if (!r) continue;
    const where = r.roomName ? ` in ${r.roomName}` : "";
    const who = r.outgoingName ? ` from ${r.outgoingName}` : "";
    items.push({
      key: `relief-pending:${r.caseId || r.roomId || r.incomingName}`,
      severity: r.stale ? 2 : 3,
      count: 1,
      label: r.stale && r.staleReason === "departed"
        ? `${r.incomingName} has not taken over${where} — ${r.outgoingName || "the provider"} has already left`
        : `${r.incomingName} is due to take over${who}${where}`,
      rooms: r.roomName ? [r.roomName] : [],
      // The takeover is confirmed on the room card or the provider's phone, not
      // from a staffing picker — offering the Fix modal here would open a screen
      // that cannot do the one thing this item is about.
      fixable: false,
    });
  }

  const unacked = (Array.isArray(emergencies) ? emergencies : []).filter(
    (e) => e && !(Array.isArray(e.acknowledgedBy) && e.acknowledgedBy.length > 0)
  );
  if (unacked.length) {
    items.push({
      key: "unacked-emergency",
      severity: 0,
      count: unacked.length,
      label: `${unacked.length} emergency ${unacked.length === 1 ? "alert" : "alerts"} unacknowledged`,
      rooms: unacked.map((e) => e.roomName || e.roomId),
    });
  }

  // ONE PROVIDER, TWO ROOMS, THE SAME MINUTES — ranked WITH "uncovered", not
  // below it, because it is the same failure wearing a disguise. An uncovered
  // room announces itself and somebody goes looking; a double-booked CRNA leaves
  // every one of those rooms LOOKING staffed, so nobody looks, while the person
  // can be in only one of them. Severity 1 buys the alarm treatment, and this is
  // what that treatment is for.
  //
  // ⚠️ AND ONLY WHILE IT IS STILL A QUESTION. `dayClockMin` drops the clashes
  // whose overlap has already ended: a card headed NEEDS YOU NOW must not carry
  // an item nobody can act on, and one that stays red until midnight teaches
  // the board runner to stop reading the card. Future clashes are kept and
  // named with their hour — "SHORT 2" without a time reads as right now, and so
  // does this. See `liveConflicts` for why the detector itself stays timeless.
  const clashes = liveConflicts(doubleBookedProviders(placed), dayClockMin);
  if (clashes.length) {
    const roomNameOf = new Map(
      (Array.isArray(lanes) ? lanes : []).map((l) => [l.roomId, l.roomName])
    );
    for (const c of clashes) {
      const when = Number.isFinite(c.startsMin) ? ` from ${to12h(c.startsMin)}` : "";
      items.push({
        key: `double-booked:${c.key}`,
        // A clash that has not started yet is a warning, not an alarm: it is
        // still true, still worth fixing, and nothing is wrong in any room this
        // minute. Alarm styling on something that is not happening is how the
        // alarm colour stops meaning anything.
        severity: when ? 2 : 1,
        count: c.roomIds.length,
        // The person is named because the fix starts with them: somebody has to
        // decide which room they keep before anything else can be arranged.
        label: `${c.name} is in ${c.roomIds.length} rooms at once${when}`,
        rooms: c.roomIds.map((id) => roomNameOf.get(id) || id),
        // Carried so the resolve flow does not re-derive the clash and risk
        // disagreeing with the warning that sent the user to it.
        conflict: c,
      });
    }
  }

  // ── OB IS UNCOVERED ────────────────────────────────────────────────────────
  //
  // ⚠️ THIS ITEM MAY NOT BE BUILT FROM `lanes` ALONE ANY MORE, AND THAT IS THE
  // WHOLE POINT. A lane exists only for a room with a CASE. The room this rule
  // was written for — WC OB, on a standing on-call CRNA — no longer draws a case
  // when nobody is in a section (domain/timeline.isStandingOnCall), so filtering
  // lanes by `uncoveredRoomIds` would have found nothing and this alarm would
  // have gone SILENT at exactly the moment it matters. `uncoveredRooms` is
  // therefore passed in already resolved, from the ASSIGNMENT rows that
  // `coverageStatus` judges, and lanes are only a fallback for callers that
  // still pass ids alone.
  //
  // IT NAMES THE PERSON AND WHERE THEY WENT. "1 room uncovered" tells a
  // board-runner that something is wrong but not what to do; "WC OB is
  // uncovered — Jim Slone is in WOR 3" tells them who to ring and which room to
  // backfill, which is the only actionable version of this fact.
  const rich = (Array.isArray(uncoveredRooms) ? uncoveredRooms : []).filter(
    (r) => r && r.roomId != null
  );
  const seen = new Set(rich.map((r) => r.roomId));
  const uncovered = [
    ...rich,
    // Lanes flagged by id but not described — kept so nothing an older caller
    // reports is dropped on the floor.
    ...(Array.isArray(lanes) ? lanes : [])
      .filter((l) => inSet(uncoveredRoomIds, l.roomId) && !seen.has(l.roomId))
      .map((l) => ({ roomId: l.roomId, roomName: l.roomName })),
  ];
  if (uncovered.length) {
    const laneIdSet = new Set((Array.isArray(lanes) ? lanes : []).map((l) => l.roomId));
    const detail = (r) => {
      // ⚠️ BEFORE THE `who` CHECK, because nobody to name IS the finding. An
      // unstaffed room has no provider by definition, and the old order made it
      // fall straight through to null — the one reason whose sentence matters
      // most would have printed as a bare room number.
      if (r.reason === "unstaffed") return "no CRNA assigned";
      const who = String(r.providerName || "").trim();
      if (!who) return null;
      if (r.reason === "covering" && r.awayRoomName) return `${who} is in ${r.awayRoomName}`;
      if (r.reason === "break") return `${who} is on a break`;
      if (r.reason === "away") return `${who} has stepped away`;
      return null;
    };
    const only = uncovered.length === 1 ? uncovered[0] : null;
    const onlyDetail = only ? detail(only) : null;
    items.push({
      key: "uncovered",
      severity: 1,
      count: uncovered.length,
      label: onlyDetail
        ? `${only.roomName || only.roomId} is uncovered — ${onlyDetail}`
        : `${uncovered.length} ${uncovered.length === 1 ? "room" : "rooms"} uncovered`,
      // With one described room the sentence above already carries everything;
      // repeating it underneath would print the same words twice in one row.
      rooms: onlyDetail
        ? []
        : uncovered.map((r) => {
            const d = detail(r);
            const name = r.roomName || String(r.roomId);
            return d ? `${name} · ${d}` : name;
          }),
      // ⚠️ NO "Fix" BUTTON WHEN THERE IS NOTHING FOR IT TO REWRITE.
      // `OutstandingFixModal` builds its plans from the case rows in `lanes`
      // (buildPlans, mode "uncovered"). An OB room sitting on a standing watch
      // has no case row, so the button would open a screen offering nothing —
      // which this panel's own doctrine calls worse than no button, because it
      // teaches people the button does not work.
      fixable: uncovered.some((r) => laneIdSet.has(r.roomId)),
    });
  }

  // SOMEBODY IS ABOUT TO WALK — A MESSAGE, NOT AN ALARM (Gonzalo, 2026-08-15).
  // The OR Board shows a "Leaving 17:00" pill on the room card inside the last
  // GO_HOME_ALERT_MINUTES; the same constant is reused so the two surfaces
  // cannot disagree about when it starts. The board shows it on the card you
  // happen to be looking at — this puts it in the one list a board-runner
  // watches, WITH THE NAME, because "a room loses cover" does not tell you who
  // to go and find.
  //
  // ⚠️ SEVERITY 3, WHICH RENDERS AS "PLAN", NOT "ACT NOW". A shift ending on
  // time is the schedule working, not a failure — nothing here needs rescuing.
  // Tiering it as ACT NOW would put a routine 5 PM alongside a room that has
  // actually lost its provider, and a board whose amber means "someone is going
  // home when they said they would" teaches people to skip amber.
  const leavingSoon = [];
  if (Number.isFinite(nowMin)) {
    for (const lane of (Array.isArray(lanes) ? lanes : [])) {
      for (const c of (Array.isArray(lane.cases) ? lane.cases : [])) {
        // ⚠️ EVERY PROVIDER IN THE ROOM, NOT JUST THE SEAT. This listed the
        // seat holder only, on the reasoning that "an orientee going home costs
        // the room no cover". The product owner reversed that on 2026-08-20 —
        // "i dont care who is orienting who, place the 2 crnas" — so a second
        // CRNA leaving is a provider leaving, and a board-runner who is not
        // told loses a person from a room they believe is doubly staffed.
        //
        // ⚠️ `label` FIRST for the seat. On an orienting row `provider` is
        // still the raw cell, so this message read "Trevor Stamand 5 orient
        // Kayla Gibbons · WOR 6 · 5 PM" — a person nobody can go and find.
        // `label` is the seat. Unchanged for every other row, where the two are
        // equal (and for a solo case, where `provider` is empty and `label` is
        // the MD).
        //
        // ⚠️ A DEPARTURE WE DO NOT KNOW IS NOT A DEPARTURE. A legacy pair
        // prints a shift code for the seat and nothing for the second name;
        // `orientee.departMin` is null there and this must stay silent rather
        // than borrow the colleague's time and announce a person is leaving at
        // an hour nobody told us.
        const people = [
          { who: c.label || c.provider || "Someone", depart: c.departMin, depart12h: c.depart12h },
          { who: c.orientee?.name, depart: c.orientee?.departMin, depart12h: c.orientee?.depart12h },
        ];
        for (const person of people) {
          if (!person.who) continue;
          // ⚠️ CHECKED PER PERSON, not once for the row. `undefined - nowMin`
          // is NaN, and NaN fails BOTH range tests below — so a row with no
          // recorded departure would have fallen through and printed a name
          // with an empty time as if somebody were about to leave.
          if (!Number.isFinite(person.depart)) continue;
          const away = person.depart - nowMin;
          // Already gone is not a warning, and a shift ending tomorrow morning
          // (on call, 31:00) is never "soon".
          if (away < 0 || away > GO_HOME_ALERT_MINUTES) continue;
          leavingSoon.push(`${person.who} · ${lane.roomName} · ${person.depart12h || to12h(person.depart)}`);
        }
      }
    }
  }
  if (leavingSoon.length) {
    items.push({
      key: "leaving-soon",
      severity: 3,
      count: leavingSoon.length,
      label: `${leavingSoon.length} ${leavingSoon.length === 1 ? "provider leaves" : "providers leave"} within ${GO_HOME_ALERT_MINUTES} min`,
      rooms: leavingSoon,
    });
  }

  // Distinct from "uncovered": the room has someone now, but the schedule says
  // they leave before the work ends. It is the item you can still do something
  // cheap about, which is why it is listed separately rather than folded in.
  //
  // ⚠️ NEVER SUPPRESSED BY THE LEAVING MESSAGE. This briefly hid this item for a
  // room whose provider was also leaving soon — a message silencing the
  // actionable warning about the same room, which is exactly backwards. The two
  // say different things: this one is "the room will be short", the message is
  // "here is who is walking".
  const lapsing = (Array.isArray(lanes) ? lanes : [])
    .map((l) => ({ lane: l, cov: laneCoverage(l) }))
    .filter(({ lane, cov }) => cov.uncovered && !inSet(uncoveredRoomIds, lane.roomId));
  if (lapsing.length) {
    items.push({
      key: "departs-before-end",
      severity: 2,
      count: lapsing.length,
      label: `${lapsing.length} ${lapsing.length === 1 ? "room loses" : "rooms lose"} cover before the case ends`,
      rooms: lapsing.map(({ lane, cov }) => `${lane.roomName} · ${cov.departLabel}`),
    });
  }

  // A NAME ON THE SHEET THAT MATCHES NOBODY IN THE ROSTER.
  //
  // ⚠️ THE ENGINE HAS ALWAYS RECORDED THESE AND NOTHING EVER SHOWED THEM. Each
  // one is a cell the identity ladder refused — deliberately, because the rule
  // is "never create, always ask" and an abbreviated name has no single right
  // answer. Nothing ever asked. On 2026-09-11 "JUS" sat unresolved as the
  // ATTENDING on WOR 11 and WC E1 for a whole morning: the sheet named a
  // doctor, the board showed none, and the record explaining exactly that was
  // in a collection no screen opened. The refusal was correct; the silence was
  // the defect.
  //
  // ⚠️ SEVERITY 2 — A WARNING, NOT AN ALARM. Tier 0-1 belongs to unacknowledged
  // emergencies and rooms running with nobody in them. A seat the board cannot
  // NAME is serious (the rules gate Room Status on `providerId` /
  // `supervisingDoctorId`, so an unnamed attending cannot acknowledge a phase)
  // but the room is staffed and the humans in it know who they are.
  //
  // ⚠️ AND IT IS NOT `fixable`. There is no screen to open that can answer this
  // safely: the fix is a person deciding which colleague "Gabi S" is, or giving
  // a doctor the short code the sheet calls them by. Offering a one-tap fix for
  // a question whose whole point is that it needs a human is how the wrong
  // person ends up in a room.
  // ⚠️ TRIMMED HERE TOO, not only in the hook. A whitespace-only cell is
  // truthy, and trusting the caller to have cleaned it is how a blank row
  // becomes “ ” matches nobody on the roster — a warning about nothing, which
  // costs the surface its credibility faster than missing one would.
  // ── THE SHEET HAS STOPPED REACHING THE BOARD ───────────────────────────────
  //
  // ⚠️ THE ROWS BEING TODAY'S IS WHY NOTHING CAUGHT THIS. The freshness gate
  // asks whether the board holds today's data, and it does — the 1 AM import
  // put it there. What died was the FEED: `sheetEngine` runs every minute, and
  // on 2026-09-14 a cleared `Start` label in the header made `detectBoardLayout`
  // refuse the tab 66 cycles running. The board showed stale staffing for five
  // hours and every surface looked healthy.
  //
  // `useSheetEngineQuestions` was right to ignore a refused run — it answers no
  // identity questions, and printing its empty list as "nothing unresolved"
  // would hide what the last good run found. The gap was that nobody reported
  // the refusal ITSELF.
  //
  // ⚠️ A REFUSAL IS NOT YET AN OUTAGE. The same day's captures show a header
  // cell flipping between `RELIEF` and blank four times in two hours — somebody
  // editing the tab. Shouting on the first refused cycle would teach people to
  // ignore this row by lunchtime, so it waits for a real gap.
  //
  // ⚠️ SEVERITY 2, NOT THE ALARM TIER. 0–1 is unacknowledged emergencies and
  // rooms running with nobody in them. The board here is still showing REAL
  // staffing — just not the newest — and a stale feed in that tier is how staff
  // learn to scroll past red ([[alarm-colour-is-finite]]).
  if (sheetFeed && sheetFeed.ok === false && Number.isFinite(nowMs)) {
    const lastOk = Number.isFinite(sheetFeed.lastOkMs) ? sheetFeed.lastOkMs : null;
    // No successful run all day is the WORST case, not the quietest — a missing
    // timestamp must never subtract to "zero minutes stale".
    const staleMin = lastOk == null ? null : Math.max(0, Math.round((nowMs - lastOk) / 60000));
    if (staleMin == null || staleMin >= SHEET_FEED_STALE_MIN) {
      const reason = String(sheetFeed.reason || "").trim();
      items.push({
        key: "sheet-feed-stopped",
        severity: 2,
        count: 1,
        label:
          staleMin == null
            ? "The sheet has not reached the board today — showing the overnight import"
            : `The sheet has not reached the board for ${staleMin} minutes`,
        // The engine's own words. A parse refusal names the cell that broke it
        // ("no header row with a Start column"), which is the difference between
        // "something is wrong" and knowing which header to put back.
        rooms: reason ? [reason] : [],
        // Nothing to open: the fix is in the spreadsheet, not in ATC.
        fixable: false,
      });
    }
  }

  const unmatchedNames = (Array.isArray(sheetQuestions) ? sheetQuestions : [])
    .map((q) => (q ? { ...q, raw: String(q.raw || "").trim() } : null))
    .filter((q) => q && q.raw);
  if (unmatchedNames.length) {
    // Grouped by the name itself: one person unresolved in three rooms is ONE
    // decision, and three list rows would read as three problems.
    const byName = new Map();
    for (const q of unmatchedNames) {
      const list = byName.get(q.raw) || [];
      list.push(q);
      byName.set(q.raw, list);
    }
    const people = [...byName.entries()];
    items.push({
      key: "sheet-unmatched-names",
      severity: 2,
      count: people.length,
      label:
        people.length === 1
          ? "1 name on the sheet matches nobody on the roster"
          : `${people.length} names on the sheet match nobody on the roster`,
      rooms: people.map(([raw, list]) => {
        const rooms = list.map((q) => q.room).filter(Boolean);
        const seat = list[0]?.seat === "attending" ? "MD" : "CRNA";
        return rooms.length ? `“${raw}” · ${seat} · ${rooms.join(", ")}` : `“${raw}” · ${seat}`;
      }),
    });
  }

  // A COVER WHO WAS NAMED AND NEVER TURNED UP.
  //
  // ⚠️ THIS ITEM IS WHAT MAKES THE `assigned` STATE SAFE TO HAVE. Assigning a
  // break takes the channel out of "none", so `breakDueState` immediately stops
  // counting it as an overdue meal — the room quietly leaves every count on the
  // board while still being uncovered. Without something watching the clock,
  // `assigned` is a state that can sit forever with nothing counting, which is
  // exactly where things go to be forgotten.
  //
  // ⚠️ SEVERITY 2, NEVER LOWER. Severity 0-1 is the ALARM tier, owned by
  // unacknowledged emergencies and rooms running with no provider. A late lunch
  // relief in that tier is how staff learn to scroll past red. It is a warning,
  // and it is not `fixable` — there is no placement screen to open; somebody
  // has to pick up a phone.
  const lateCovers = [];
  if (Number.isFinite(nowMin)) {
    for (const row of (Array.isArray(breakRows) ? breakRows : [])) {
      if (!row) continue;
      for (const channel of BREAK_CHANNELS) {
        if ((row.breaks && row.breaks[channel]) !== "assigned") continue;
        const since = hmToMinutes((row.breakTimes || {})[channel]);
        if (since == null) continue;
        const waited = nowMin - since;
        // A negative age is a clock problem, not a late cover.
        if (waited < coverLateAfter(channel)) continue;
        const who = row.relieverName || "somebody";
        lateCovers.push(
          `${row.roomName || row.roomId} · ${channel} · ${who} · ${waited}m`
        );
      }
    }
  }
  if (lateCovers.length) {
    items.push({
      key: "cover-not-arrived",
      severity: 2,
      count: lateCovers.length,
      label: `${lateCovers.length} ${lateCovers.length === 1 ? "room is" : "rooms are"} waiting on a cover who has not arrived`,
      rooms: lateCovers,
      fixable: false,
    });
  }

  // A DIRECTIVE NOBODY HAS ANSWERED.
  //
  // ⚠️ SILENCE IS UNINTERPRETABLE, AND NOTHING WAS COUNTING IT. An unanswered
  // directive rendered as grey "waiting" on the task card and entered no list
  // anywhere — there was no escalation on an unacknowledged task in the whole
  // codebase. From the board, that silence is indistinguishable from four
  // different things: not seen, no signal, scrubbed in, or refusing. The card
  // said "waiting" forever and the AIC found out by walking to the room.
  //
  // ⚠️ SEVERITY 2, NEVER LOWER — the same reasoning as `cover-not-arrived`
  // directly above. Severity 0-1 is the ALARM tier, owned by unacknowledged
  // emergencies and rooms running with no provider. Routine board traffic in
  // that tier is how staff learn to scroll past red. Not `fixable` either:
  // there is no screen to open, somebody has to pick up a phone.
  //
  // ⚠️ AND A REFUSAL IS LISTED SEPARATELY, NOT FOLDED IN. "Nobody answered" and
  // "somebody said no, here is why" call for completely different actions —
  // chase, versus find another person — and a refusal is the more actionable of
  // the two, because it is already certain.
  const unanswered = [];
  const refused = [];
  for (const task of (Array.isArray(tasks) ? tasks : [])) {
    if (!task || !requiresConfirm(task)) continue;
    const where = task.roomName ? `${task.roomName} · ` : "";

    // ⚠️ A REFUSAL NEEDS NO CLOCK. It is already certain the moment it is
    // written, so it must surface even on a board with no clock at all.
    for (const uid of recipientsOf(task)) {
      if (answerOf(task, uid) !== "declined") continue;
      const refusal = declineOf(task, uid);
      const who = nameByUid[uid] || "somebody";
      refused.push(`${where}${who} · ${refusal?.reason || "said no"}`);
    }

    const waiting = pendingUids(task);
    if (!waiting.length) continue;
    // No readable stamp is not a late directive — it is a directive whose
    // serverTimestamp has not landed yet, which is one frame old. And with no
    // instant to measure against, no age can be claimed at all.
    const waited = taskWaitedMin(task, nowMs);
    if (waited == null) continue;
    // A negative age is a clock problem, not an unanswered directive.
    if (waited < UNANSWERED_AFTER_MIN) continue;
    // ⚠️ THE CEILING IS FOR BREAK COVERS ONLY. The argument for retiring an old
    // nag is that the ROOM is still watched by `cover-not-arrived` above — and
    // that item only knows about breaks. A hand-typed "come to WOR 3" that
    // nobody answers has nothing else watching it, so dropping it at 121 minutes
    // would lose the fact outright. `requiresConfirm` is true for almost every
    // kind, so scoping on it would have retired all of them.
    if (task.autoReason === AUTO_REASONS.BREAK_COVER && waited > UNANSWERED_STALE_MIN) continue;
    const who = waiting.map((uid) => nameByUid[uid] || "somebody").join(", ");
    unanswered.push(`${where}${who} · ${waited}m`);
  }
  if (refused.length) {
    items.push({
      key: "directive-declined",
      severity: 2,
      count: refused.length,
      label: `${refused.length} ${refused.length === 1 ? "person has" : "people have"} said they cannot come`,
      rooms: refused,
      fixable: false,
    });
  }
  if (unanswered.length) {
    items.push({
      key: "directive-unanswered",
      severity: 2,
      count: unanswered.length,
      label: `${unanswered.length} ${unanswered.length === 1 ? "directive has" : "directives have"} had no answer — call them`,
      rooms: unanswered,
      fixable: false,
    });
  }

  const dueBreaks = [];
  for (const row of (Array.isArray(breakRows) ? breakRows : [])) {
    if (!row) continue;
    for (const channel of ["breakfast", "lunch", "dinner"]) {
      const state = row.breaks && row.breaks[channel] ? row.breaks[channel] : "none";
      if (breakDueState(state, channel, breakWindows, nowMin) === "due") {
        dueBreaks.push(`${row.providerName || row.roomName || row.roomId} · ${channel}`);
      }
    }
  }
  if (dueBreaks.length) {
    items.push({
      key: "break-overdue",
      severity: 3,
      count: dueBreaks.length,
      label: `${dueBreaks.length} meal ${dueBreaks.length === 1 ? "break" : "breaks"} overdue`,
      rooms: dueBreaks,
    });
  }

  const missingMd = Array.isArray(roomsMissingMd) ? roomsMissingMd.filter(Boolean) : [];
  if (missingMd.length) {
    items.push({
      key: "no-supervising-md",
      severity: 4,
      count: missingMd.length,
      label: `${missingMd.length} ${missingMd.length === 1 ? "room has" : "rooms have"} no supervising MD`,
      rooms: missingMd.map((r) => r.roomName || r.roomId),
    });
  }

  const short = shortBoundariesAhead(staffing, nowMin);
  if (short.length) {
    items.push({
      key: "staffing-short",
      severity: 5,
      count: short.length,
      label: `Short-staffed at ${short.map((s) => s.label).join(", ")}`,
      rooms: short.map((s) => `${s.label} · ${s.delta}`),
    });
  }

  return items.sort((a, b) => a.severity - b.severity);
}

export { SHIFT_BOUNDARIES };
